import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ProfileCompletionCard } from "@/components/nursing-homes/owner/dashboard/ProfileCompletionCard";
import { BasicInfoForm } from "@/components/nursing-homes/owner/dashboard/profile/BasicInfoForm";
import { FacilitiesForm } from "@/components/nursing-homes/owner/dashboard/profile/FacilitiesForm";
import { RoomsForm } from "@/components/nursing-homes/owner/dashboard/profile/RoomsForm";

export default function ProfilePage() {
  const { nursingHome } = useOutletContext<{ nursingHome: NursingHome }>();
  console.log("ProfilePage nursingHome context:", nursingHome);

  if (!nursingHome) {
    return (
      <div className="p-8 text-center text-red-600">
        No nursing home data found. Please check your route/context setup.<br/>
        (If you see this, the Profile page is not receiving the expected context.)
      </div>
    );
  }

  const [isSavingBasic, setIsSavingBasic] = useState(false);
  const [isSavingFacilities, setIsSavingFacilities] = useState(false);
  const [isSavingRooms, setIsSavingRooms] = useState(false);

  const handleSave = async (formData: Partial<NursingHome>, section: 'basic' | 'facilities' | 'rooms') => {
    try {
      console.log("Starting save operation for section:", section);
      console.log("Form data:", formData);
      console.log("Nursing home slug:", nursingHome.nh_slug);
      
      if (section === 'basic') setIsSavingBasic(true);
      if (section === 'facilities') setIsSavingFacilities(true);
      if (section === 'rooms') setIsSavingRooms(true);

      // Check if user has permission to update this nursing home
      const { data: claimCheck, error: claimError } = await supabase
        .from('nursing_home_claims')
        .select('status, user_id')
        .eq('nursing_home_id', nursingHome.nh_slug)
        .eq('status', 'approved')
        .single();

      console.log("Claim check result:", claimCheck, claimError);

      const updateData: Record<string, any> = { ...formData };
      if ('deposit_amount' in updateData && typeof updateData.deposit_amount === 'string' && updateData.deposit_amount) {
        updateData.deposit_amount = parseFloat(updateData.deposit_amount);
      }
      
      console.log("Update data being sent:", updateData);
      
      const { data, error } = await supabase
        .from('nursing_homes')
        .update(updateData)
        .eq('nh_slug', nursingHome.nh_slug)
        .select()
        .single();
        
      console.log("Update result:", { data, error });
      
      if (error) throw error;
      toast.success("Profile updated successfully");
      return data as NursingHome;
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(`Failed to update profile: ${error.message}`);
      throw error;
    } finally {
      if (section === 'basic') setIsSavingBasic(false);
      if (section === 'facilities') setIsSavingFacilities(false);
      if (section === 'rooms') setIsSavingRooms(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Profile Management</h1>
          <p className="text-muted-foreground">
            Update your nursing home's profile information
          </p>
        </div>
        <ProfileCompletionCard nursingHome={nursingHome} />
      </div>
      <div className="space-y-8">
        <section>
          <h2 className="text-xl font-semibold mb-2">Basic Information</h2>
          <BasicInfoForm
            nursingHome={nursingHome}
            onSave={(data) => handleSave(data, 'basic')}
            isSaving={isSavingBasic}
          />
        </section>
        <section>
          <h2 className="text-xl font-semibold mb-2">Facilities & Amenities</h2>
          <FacilitiesForm
            nursingHome={nursingHome}
            onSave={(data) => handleSave(data, 'facilities')}
            isSaving={isSavingFacilities}
          />
        </section>
        <section>
          <h2 className="text-xl font-semibold mb-2">Rooms & Pricing</h2>
          <RoomsForm
            nursingHome={nursingHome}
            onSave={(data) => handleSave(data, 'rooms')}
            isSaving={isSavingRooms}
          />
        </section>
      </div>
    </div>
  );
}
